import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MapService } from '@ebw-monorepo/map';
import { EbwModalService } from '@ebw-monorepo/modal';
import { debounceTime, distinctUntilChanged, filter, lastValueFrom, Subject, switchMap, takeUntil } from 'rxjs';
import { PsaService } from '../../../../services/psa.service';
import { Entity } from '../psa-configurator.model';
import { MatAutocomplete } from '@angular/material/autocomplete';

/**
 * @Author Tommaso Lazzaretti
 *
 * Component responsible for managing the duplication of PSA (Piano Sicurezza Acque) entities.
 * This component allows users to draw, modify, and select geometries (specifically circles) on the map,
 * and duplicate PSA information to nearby selected entities.
 *
 * The component interacts with various services for handling map interactions, drawing tools, and PSA duplication logic.
 * It manages temporary vector layers for the features drawn and provides an interface for manual adjustments of geometries.
 *
 * @class PsaCreateInterconnectionComponent
 * @implements {OnInit, OnDestroy}
 */
@Component({
  selector: 'ebw-monorepo-psa-create-interconnection',
  templateUrl: './psa-create-interconnection.component.html',
  styleUrls: ['./psa-create-interconnection.component.scss'],
  animations: [
    trigger('fadeAnimation', [
      state('void', style({ opacity: 0 })), // Stato iniziale (quando l'elemento non è presente)
      transition(':enter', [
        // Quando l'elemento entra
        style({ opacity: 0 }), // Partiamo da opacità 0
        animate('500ms ease-out', style({ opacity: 1 })) // Animiamo a opacità 1
      ]),
      transition(':leave', [
        // Quando l'elemento esce
        animate('500ms ease-in', style({ opacity: 0 })) // Animiamo a opacità 0
      ])
    ])
  ],
  encapsulation: ViewEncapsulation.None
})
export class PsaCreateInterconnectionComponent implements OnInit, OnDestroy {
  form: FormGroup;
  public searchResultsCampo1: any[] = [];
  public searchResultsCampo2: any[] = [];
  public autocompleted: boolean = false;
  private destroy$ = new Subject<void>();

  psaObject: Entity = null;

  constructor(
    private _fb: FormBuilder,
    private _psaService: PsaService,
    private _mapService: MapService,
    private _ebwModalService: EbwModalService
  ) {}

  ngOnInit(): void {
    this._initForm();
  }

  @ViewChild('autoCampo1') auto!: MatAutocomplete;

  selectOptionManually(value: string) {
    const option = this.auto.options.find((o) => o.value === value);
    option?.select();
    const control = this.form.get('startNode');
    control.setErrors(null);
  }

  // ------------------------------------------------------------------------------------------------

  private _initForm() {
    this.form = this._fb.group({
      startNode: ['', [Validators.required, this.checkIfOptionSelectedStartNode.bind(this)]],
      endNode: ['', [Validators.required, this.checkIfOptionSelectedEndNode.bind(this)]],
      nome: ['', [Validators.required, Validators.minLength(3)]]
    });

    this._setupAutocomplete('startNode', 'searchResultsCampo1');
    this._setupAutocomplete('endNode', 'searchResultsCampo2');

    this._psaService.startNode$.pipe(takeUntil(this.destroy$)).subscribe({
      next: async (node) => {
        if (!node) {
          return;
        }
        const control = this.form.get('startNode');
        control?.setValue(node.search_term, { emitEvent: false });
        this.searchResultsCampo1 = await lastValueFrom(this._psaService._getListElements(node.search_term, '', 'nodo'));
        setTimeout(() => {
          this.selectOptionManually(node.search_term);
        }, 500);
        this.psaObject = node;
      }
    });
  }

  // ------------------------------------------------------------------------------------------------

  checkIfOptionSelectedStartNode(control: any) {
    try {
      const nodeInvalid = !this.searchResultsCampo1.find((obj) => obj.search_term === control.value);
      return nodeInvalid ? { optionError: true } : null;
    } catch (error) {
      console.error('[checkIfOptionSelectedStartNode]', error);
      return null;
    }
  }

  // ------------------------------------------------------------------------------------------------

  checkIfOptionSelectedEndNode(control: any) {
    try {
      const nodeInvalid = !this.searchResultsCampo2.find((obj) => obj.search_term === control.value);
      return nodeInvalid ? { optionError: true } : null;
    } catch (error) {
      console.error('[checkIfOptionSelectedEndNode]', error);
      return null;
    }
  }

  // ------------------------------------------------------------------------------------------------

  public onItemSelect(value, control): void {
    this.autocompleted = true;
    this.form.get(control)?.setValue(value);
  }

  // ------------------------------------------------------------------------------------------------

  private _setupAutocomplete(controlName: string, resultsArrayName: string) {
    // mi sottoscrivo ai cambiamenti dell'autocomplete per avviare la ricerca su solr
    this.form
      .get(controlName)
      .valueChanges.pipe(
        // tempo minimo tra una richiesta e l'altra
        debounceTime(300),
        filter((value: string) => {
          if (this.autocompleted) {
            this.autocompleted = false;
            return false;
          }

          return true;
        }),
        // cerco solo con almeno 3 caratteri
        filter((value: string) => value.length > 2),
        // solo se il testo cambia
        distinctUntilChanged(),
        // ricarca solr
        switchMap((value: string) => this._psaService._getListElements(value, '', 'nodo'))
      )
      .subscribe({
        next: (results: any[]) => {
          this.autocompleted = false;
          // pulisco eventuali errori rimasti
          // this.form.get(controlName).setErrors(null);
          // Se non ho trovato risultati
          if (!results.length) {
            this[resultsArrayName] = [];
            return;
          }
          // altrimenti popolo le opzioni dell'autocomplete
          this[resultsArrayName] = results;
        },
        error: (error) => {
          // setto eventuali errori e pulisco l'array
          console.error('[CreateInterconnection][getListElement]', error);
          this[resultsArrayName] = [];
          this.form.get(controlName).setErrors({ queryError: true });
          // mi risottoscrivo altrimenti le ricerche successive non funzionano
          this._setupAutocomplete(controlName, resultsArrayName);
        }
      });
  }

  // ------------------------------------------------------------------------------------------------

  async saveInterconnection() {
    if (this.form.invalid) {
      return;
    }

    const title = 'Creazione interconnessione';
    const { startNode: nodo1, endNode: nodo2, nome } = this.form.value;
    if (nodo1 === nodo2) {
      const content = 'Non puoi selezionare lo stesso nodo due volte';
      this._ebwModalService.openWarningModal({ title, content });
    }
    try {
      const request = this._psaService.createInterconnection({ nodo1, nodo2, nome });
      await lastValueFrom(request);
      const content = 'Operazione eseguita con successo';
      this._ebwModalService.openInfoModal({ title, content });
      this._mapService.clearLayerCache();
      this.closeModal();
    } catch (error) {
      console.log('[psaCreateInterconnection] [saveInterconnection] ', error);
      this._ebwModalService.openErrorModal({ title, content: error?.error?.message });
    }
  }

  // ------------------------------------------------------------------------------------------------

  /**
   * Close the modal and reset the drawing tool.
   */
  closeModal() {
    this._psaService.closeCreateInterconnectionPanel();
  }

  // ------------------------------------------------------------------------------------------------

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
